<template>
  <div class="col-12 my-5 position-relative message-container cursor-pointer"
       :class="[
           { 'align-self-start' : !isSender },
           { 'align-self-end' : isSender },
           { 'selected' : isSelected },
       ]">
    <div class="d-flex m-0" :class="{ 'justify-content-end' : isSender }">
      <div class="col-2 col-md-1 col-lg-1 d-flex align-self-stretch" :class="{ 'order-2 justify-content-end': isSender }">
        <ManyAvatar :size="1" :data="getUser"/>
      </div>
      <div class="mw-lg-400px align-content-center position-relative"
           :class="[
               { 'send' : isSender },
               { 'receive' : !isSender },
           ]">
        <span style="white-space: pre-line !important;" class="fw-bold">
          {{ message.content }}
          <span class="d-flex justify-content-end" style="font-size: 8px;">{{ message?.created_at ? getDateHour(message?.created_at) : '' }}</span>
        </span>
        <span class="d-flex justify-content-end align-items-center text-muted mb-n10"
              :class="[{ 'end-0': isSender }, { 'start-0': !isSender }]">
          
          <span class="mt-1 ms-5 react" :title="$t('feed.react')">
            <div class="d-flex">
              <div v-if="message.reactions" class="d-flex dropdown">
                <div v-for="(emoji, index) in message.reactions" :title="$t('button.view')" data-bs-toggle="dropdown" aria-expanded="false" style="cursor: pointer;"
                    :key="index"
                    v-html="index" 
                    class="fs-2 me-2"
                >
                </div>
                <ul class="dropdown-menu dropdown-menu-end shadow" style="max-height: 400px; overflow-y: auto; width: 250px;">
                    <li>
                        <span class="px-5 d-flex justify-content-between">
                            <div class="d-flex" v-for="(emoji, index) in message.reactions" :key="index">
                                <span class="me-2 mt-1">{{ emoji?.count }}</span>
                                <span v-html="index" class="fs-2"></span>
                            </div>
                        </span>
                    </li>
                    <li v-for="(user, index) in message.user_reactions" :key="index">
                        <span class="px-5 d-flex justify-content-between">
                            <span>{{ user?.name }}</span>
                            <span v-html="user?.type" class="fs-2"></span>
                        </span>
                    </li>
                </ul>
              </div>
              <div class="d-flex justify-content-center align-items-center card shadow-sm px-2" style="width: 20; height: 20px;">
                <div class="dropdown">
                  <div data-bs-toggle="dropdown" aria-expanded="false" style="cursor: pointer;">
                    <i class="fas fa-smile text-yellow"></i>
                  </div>
                  <ul class="dropdown-menu dropdown-menu-end shadow">
                    <li>
                      <a class="dropdown-item" href="#">
                        <span class="me-3 fs-2" :title="emoji[locale]" 
                              @click="selectCommentEmoji(emoji.value)" 
                              v-for="(emoji, index) in emojis" 
                              :key="index" 
                              v-html="emoji.value"></span>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>  
          </span>
          <div v-if="message.mentions?.length > 0" class="d-flex justify-content-center align-items-center card shadow-sm px-2 ms-3" style="width: 20px; height: 20px;">
              <div :title="$t('feed.mentions')" data-bs-toggle="dropdown" aria-expanded="false" style="cursor: pointer;">
                  <i class="fas fa-users"></i>
              </div>
              <ul class="dropdown-menu dropdown-menu-end shadow mt-2" style="max-height: 300px; overflow-y: auto; width: 150px;">
                  <li v-for="(user, index) in message.mentions" :key="index">
                      <span class="px-5">
                          <span>{{ user?.name }}</span>
                      </span>
                  </li>
              </ul>
          </div>
          <span class="ms-3 answer" @click="answerComment">{{ $t('feed.answer') }}</span>
        </span>
      </div>
    </div>
    <i @click.prevent="share" class="share-btn fa fa-share position-absolute top-50 cursor-pointer"
       :class="[
        { 'me-n7 end-0 ' : !isSender },
        { 'ms-n7 share-sender-rotate' : isSender }
        ]">
    </i>
  </div>
  <div v-if="message.replies && message.replies.length" class="replies">
    <Message v-for="(reply, index) in message.replies" 
              :key="index" 
              :message="reply" 
              :emojis="emojis" 
              :isSelected="false" 
              @reply="answerReply(reply)"/>
  </div>
</template>

<script setup>
import ManyAvatar from "@/components/ManyAvatar";
import { useStore } from "vuex";
import { ref, computed } from "vue";
import { useLocales } from "@/composables/locales";

const emits = defineEmits(["reply", "select"]);

const { locale } = useLocales();
const props = defineProps({
  message: {
    type: Object,
    required: true
  },
  emojis: {
    type: Array,
    default: () => []
  },
  isSelected: { type: Boolean, default: false }
});

const store = useStore();

const currentUser = computed(() => store.getters['user/currentUser']);
const getUser = computed(() => {
  const fullname = props.message?.commentator?.fullname?.split(' ') || currentUser.value?.fullname?.split(' ');
  return fullname ? [{ firstname: fullname[0], lastname: fullname[1] }] : [];
});
const isSender = computed(() => props.message?.user_id === currentUser.value?.id);

function selectCommentEmoji(item) {
  emits('select', item, props.message?.id)
}

function getDateHour(date) {
  return new Date(date).toLocaleTimeString(locale.value, {
    hour: '2-digit',
    minute: '2-digit'
  });
}

function answerComment() {
  emits('reply', props.message);
}

function answerReply(reply) {
  emits('reply', reply);
}
</script>

<style lang="scss">
.send {
  padding: 15px;
  border-radius: 10px 10px 0 10px;
  background-color: #0072CE0D;
  min-height: 15px;
}

.receive {
  padding: 5px 15px;
  border-radius: 10px 10px 10px 0;
  background-color: #F7F7F7;
  min-height: 15px;
  width: 50%;
  margin-bottom: 5px;
}

.replies {
  margin-left: 8%;
}

.answer:hover {
  color: var(--bs-primary) !important;
}

.text-yellow {
  color: var(--bs-yellow) !important;
}

.share-btn {
  display: none;
}

.message-container:active, .message-container.selected {
  background-color: var(--bs-light-primary);
}

.message-container.selected .share-btn {
  display: block;
}

.share-sender-rotate {
  transform: rotateY(180deg);
}
</style>
